import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColumnState } from 'ag-grid-community';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { SAL2050_R01TabType } from './Store_SAL2050_R01';
import { getTabGridOptions } from './components/ColumnDefinitions';
import useSAL2050_R01Store from './Store_SAL2050_R01';
import { aggridHeaderClass } from 'src/components/common/common';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { GridRowData, defaultGridRowData } from './Dto_SAL2050_R01';
import {
	isSubtotalRow,
	isCustomerSubtotalRow,
	isItemSubtotalRow,
	mergeDataWithSubtotals,
	getSubtotalCalculator,
} from './utils/subtotalUtils';
import { formatNumber } from 'src/utils/number/numberUtils';
import { getDetailRowId, getSubtotalRowId, SAL2050_R01_TABS } from './utils/tabConfig';

const AMOUNT_COLS = ['prevYearSalesAmt', 'currYearSalesAmt', 'diffAmt'];
const RATE_COLS = ['diffRate'];
const NUMERIC_COLS = [...AMOUNT_COLS, ...RATE_COLS];
const BOTTOM_LABEL_COLS = new Set(['compCd']);

const normalizeHeaderClass = (defs: ColDef[]): ColDef[] =>
	defs.map((def: ColDef & { children?: ColDef[] }) => {
		const mergedHeaderClass = [aggridHeaderClass, def.headerClass].filter(Boolean).join(' ');
		const children = Array.isArray(def.children) && def.children.length > 0
			? normalizeHeaderClass(def.children)
			: undefined;
		return { ...def, headerClass: mergedHeaderClass, suppressMenu: true, children };
	});

const applyPinnedCenterAlign = (defs: ColDef[]): ColDef[] =>
	defs.map((def: ColDef & { colId?: string; cellStyle?: ColDef['cellStyle'] }) => {
		if (!def?.colId || !BOTTOM_LABEL_COLS.has(def.colId)) return def;
		const prevCellStyle = def.cellStyle;
		const cellStyle = (params: { node?: { rowPinned?: string | null } }) => {
			if (params?.node?.rowPinned === 'bottom') {
				return {
					...(typeof prevCellStyle === 'function' ? prevCellStyle(params as never) : prevCellStyle),
					textAlign: 'center',
				};
			}
			return typeof prevCellStyle === 'function' ? prevCellStyle(params as never) : prevCellStyle;
		};
		return { ...def, cellStyle };
	});

const calcDiffRate = (prevYearSalesAmt: number, currYearSalesAmt: number): number => {
	if (prevYearSalesAmt === 0) return currYearSalesAmt === 0 ? 0 : 100;
	return ((currYearSalesAmt - prevYearSalesAmt) / prevYearSalesAmt) * 100;
};

interface StockGridBaseProps {
	gridRef: React.RefObject<AgGridReact<any> | null>;
	tabType: SAL2050_R01TabType;
	data: any[];
	columns: ColDef[];
	onTabChange: (tab: SAL2050_R01TabType) => Promise<void>;
}

export const Table_SAL2050_R01: React.FC<StockGridBaseProps> = ({
	gridRef,
	tabType,
	data,
	columns,
	onTabChange
}) => {
	const {
		SAL2050_R01activeTab,
		SAL2050_R01tabColumnStates,
		setSAL2050_R01TabColumnState,
		SAL2050_R01tabSelectedIds,
		setSAL2050_R01TabSelectedIds,
		SAL2050_R01showSubtotals,
		SAL2050_R01tabSubtotalData,
		setSAL2050_R01TabSubtotalData,
		SAL2050_R01tabGridData,
		setSAL2050_R01TabGridData,
		SAL2050_R01tabScrollTop,
		setSAL2050_R01TabScrollTop,
	} = useSAL2050_R01Store();

	const [gridKey, setGridKey] = useState(0);
	const [, setHoveredRowIndex] = useState<number | null>(null);

	useEffect(() => {
		setGridKey(prev => prev + 1);
	}, [tabType]);

	useEffect(() => {
		if (data && data.length > 0) {
			const calculateSubtotals = getSubtotalCalculator(tabType);
			const subtotals = calculateSubtotals(data);
			setSAL2050_R01TabSubtotalData(tabType, subtotals);

			const mergedData = mergeDataWithSubtotals(data, subtotals, SAL2050_R01showSubtotals, tabType);
			setSAL2050_R01TabGridData(tabType, mergedData);
		} else {
			setSAL2050_R01TabSubtotalData(tabType, []);
			setSAL2050_R01TabGridData(tabType, []);
		}
	}, [data, tabType, SAL2050_R01showSubtotals, setSAL2050_R01TabSubtotalData, setSAL2050_R01TabGridData]);

	useEffect(() => {
		if (data && data.length > 0 && SAL2050_R01tabSubtotalData[tabType].length > 0) {
			const mergedData = mergeDataWithSubtotals(data, SAL2050_R01tabSubtotalData[tabType], SAL2050_R01showSubtotals, tabType);
			setSAL2050_R01TabGridData(tabType, mergedData);
		}
	}, [SAL2050_R01showSubtotals, data, tabType, SAL2050_R01tabSubtotalData, setSAL2050_R01TabGridData]);

	const currentGridData = SAL2050_R01tabGridData[tabType] || [];

	const localStorageKey = useMemo(() => `SAL2050_R01_${tabType}_columns`, [tabType]);
	const tabs = SAL2050_R01_TABS;

	const handleTabClick = useCallback(async (tab: SAL2050_R01TabType) => {
		if (tab === SAL2050_R01activeTab) return;
		await onTabChange(tab);
	}, [onTabChange, SAL2050_R01activeTab]);

	const getRowId = useCallback((params: any) => {
		const row = params.data;

		if (isSubtotalRow(row)) {
			return getSubtotalRowId(tabType, row.groupKey);
		}

		return getDetailRowId(tabType, row) || params.node.id;
	}, [tabType]);

	const makeRowId = useCallback((row: any) => {
		if (isSubtotalRow(row)) {
			return getSubtotalRowId(tabType, row.groupKey);
		}

		return getDetailRowId(tabType, row);
	}, [tabType]);

	const getCellStyle = useCallback((params: any, key: string) => {
		if (isSubtotalRow(params.data)) {
			const isNumericColumn = NUMERIC_COLS.includes(key);

			if (isCustomerSubtotalRow(params.data)) {
				return {
					backgroundColor: '#f0f8ff',
					color: '#2c5aa0',
					fontWeight: 'bold',
					borderTop: '2px solid #2c5aa0',
					borderBottom: '1px solid #2c5aa0',
					textAlign: isNumericColumn ? 'right' : 'left',
				};
			}

			if (isItemSubtotalRow(params.data)) {
				return {
					backgroundColor: '#fff8f0',
					color: '#a05a2c',
					fontWeight: 'bold',
					borderTop: '2px solid #a05a2c',
					borderBottom: '1px solid #a05a2c',
					textAlign: isNumericColumn ? 'right' : 'left',
				};
			}
		}
		return undefined;
	}, []);

	const baseColumnDefs: ColDef<GridRowData>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		...columns.map(col => ({
			...col,
			cellStyle: (params: any) => getCellStyle(params, col.colId || ''),
			cellRenderer: (params: any) => {
				if (isSubtotalRow(params.data)) {
					if (col.colId === 'customCd' && isCustomerSubtotalRow(params.data)) {
						return `${params.data.groupLabel} (${params.data.count}건)`;
					}
					if (col.colId === 'itemCd' && isItemSubtotalRow(params.data)) {
						return `${params.data.groupLabel} (${params.data.count}건)`;
					}
					if (AMOUNT_COLS.includes(col.colId || '')) {
						return formatNumber(params.data[col.colId as keyof typeof params.data] || 0);
					}
					if (RATE_COLS.includes(col.colId || '')) {
						return Number(params.data.diffRate || 0).toLocaleString(undefined, {
							minimumFractionDigits: 1,
							maximumFractionDigits: 1,
						});
					}
					if (col.colId === 'customNm' && isCustomerSubtotalRow(params.data)) {
						return params.data.customNm || '';
					}
					if (col.colId === 'itemNm' && isItemSubtotalRow(params.data)) {
						return params.data.itemNm || '';
					}
					if (col.colId === 'itemSpec' && isItemSubtotalRow(params.data)) {
						return params.data.itemSpec || '';
					}
					return '';
				}
				if (col.valueFormatter && typeof col.valueFormatter === 'function') {
					return col.valueFormatter(params);
				}
				return params.value ?? '';
			},
		})),
	] as ColDef<GridRowData>[], [columns, getCellStyle]);

	const effectiveColumnState = useMemo(() => {
		const savedState = SAL2050_R01tabColumnStates[tabType];
		return Array.isArray(savedState) && savedState.length === 0 ? null : savedState;
	}, [SAL2050_R01tabColumnStates, tabType]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs = useMemo(() => {
		const mergedColumnDefs = effectiveColumnState === null
			? baseColumnDefs
			: GridCustomColumnDefs({
				baseColumnDefs,
				columnState: effectiveColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			});
		return applyPinnedCenterAlign(normalizeHeaderClass(mergedColumnDefs));
	}, [baseColumnDefs, effectiveColumnState]);

	const handleSetColumnState = useCallback((state: ColumnState[] | null) => {
		setSAL2050_R01TabColumnState(tabType, state || []);
	}, [setSAL2050_R01TabColumnState, tabType]);

	const localDefaultColDef = useMemo(() => ({
		...defaultColDef,
		suppressMenu: true,
		headerClass: aggridHeaderClass,
	}), []);

	const rowClassRules = useMemo(() => {
		const selectedList = SAL2050_R01tabSelectedIds[tabType] || [];
		return {
			selected: (params: any) => {
				if (params.node.rowPinned === 'bottom') return false;
				if (isSubtotalRow(params.data)) return false;
				const id = makeRowId(params.data);
				return selectedList.includes(id);
			},
			hover: (params: any) => {
				if (params.node.rowPinned === 'bottom') return false;
				if (!params.data) return false;
				if (isSubtotalRow(params.data)) return false;
				const id = makeRowId(params.data);
				return !selectedList.includes(id);
			},
		} as Record<string, (p: any) => boolean>;
	}, [SAL2050_R01tabSelectedIds, tabType, makeRowId]);

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		selectedIds: SAL2050_R01tabSelectedIds[tabType] || [],
		setSelectedIds: (ids: Array<string | number>) => setSAL2050_R01TabSelectedIds(tabType, ids.map(String)),
		resolveId: (row: GridRowData) => makeRowId(row),
		isRowSelectable: (event: any) => !isSubtotalRow(event.data),
	}), [SAL2050_R01tabSelectedIds, tabType, setSAL2050_R01TabSelectedIds, makeRowId]);

	const pinnedBottomRowData = useMemo(() => {
		if (!Array.isArray(currentGridData) || currentGridData.length === 0) return [] as any[];

		const detailRows = currentGridData.filter(row => !isSubtotalRow(row));
		const number = (v: any) => Number(v) || 0;
		const dataLength = detailRows?.length || 0;
		const prevYearSalesAmt = detailRows.reduce((s, r: any) => s + number(r.prevYearSalesAmt), 0);
		const currYearSalesAmt = detailRows.reduce((s, r: any) => s + number(r.currYearSalesAmt), 0);
		const diffAmt = detailRows.reduce((s, r: any) => s + number(r.diffAmt), 0);

		return [{
			compCd: `${dataLength.toLocaleString()}건`,
			prevYearSalesAmt,
			currYearSalesAmt,
			diffAmt,
			diffRate: calcDiffRate(prevYearSalesAmt, currYearSalesAmt),
		}];
	}, [currentGridData]);

	return (
		<div className="w-full h-full flex flex-col">
			<div
				className="flex items-end mb-[0.1px]  bg-white h-[25px] "
				role="tablist"
				aria-label="전년대비실적 조회 탭"
			>
				{tabs.map((tab) => {
					const isActive = SAL2050_R01activeTab === (tab.id as SAL2050_R01TabType);
					return (
						<button
							key={tab.id}
							onClick={() => handleTabClick(tab.id as SAL2050_R01TabType)}
							role="tab"
							aria-selected={isActive}
							className={`h-[26px] px-3 text-[12px] font-medium transition-colors border border-gray-300 border-b-0
                                ${isActive
									? 'bg-white text-black-700 -mb-px shadow-sm'
									: 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                            `}
						>
							{tab.label}
						</button>
					);
				})}
			</div>

			<div className="flex-1">
				<GridContextWrapper<GridRowData>
					enableCellCalculation
					gridRef={gridRef}
					paddedGridData={currentGridData}
					initSelected={() => {}}
					defaultRowData={defaultGridRowData}
					baseColumnDefs={effectiveColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={handleSetColumnState}
					localStorageKey={localStorageKey}
					menuItems={undefined}
				>
					<Grid_Touch<GridRowData>
						key={gridKey}
						ref={gridRef as any}
						rowData={currentGridData}
						columnDefs={columnDefs}
						defaultColDef={localDefaultColDef}
						getRowId={getRowId}
						onCellKeyDown={undefined}
						rowClassRules={rowClassRules}
						pinnedBottomRowData={pinnedBottomRowData}
						shiftRangeSelection={shiftRangeSelectionConfig}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={localStorageKey}
						setColumnState={handleSetColumnState}
						isRowSelectable={(node: any) => !!node.data && !isSubtotalRow(node.data as GridRowData)}
						scrollTop={SAL2050_R01tabScrollTop[tabType]}
						setScrollTop={(top) => setSAL2050_R01TabScrollTop(tabType, top)}
						{...getTabGridOptions()}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
